import styled, {useTheme, css} from 'styled-components/native';
import React, {useCallback, useEffect, useState} from 'react';
import Animated, {FadeInDown, FadeOutUp} from 'react-native-reanimated';
import {
  Platform,
  StatusBar,
  TouchableOpacity,
  View,
  ScrollView,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {connect, useSelector} from 'react-redux';
import {RootState, store} from '../../../../redux/store';
import {Theme} from '../../../../theme/lightTheme';
import {ThreadItemType} from '../../../../interfaces/api/qna';
import {tr} from '../../../../translations';
import MainLayout from '../../../mainLayout';
import screens from '../../../../config/screens';
import {useCustomHeaderHeight} from '../../../../utils/hooks/useHeaderHeight';
import {EnterClinicalCaseProps} from '../../../../interfaces/screens/enterClinicalCase';
import ChatHeader from '../../../../components/chat/chatHeader';
import {toggleSettingsModal} from '../../../../connected-components/settingsModal/actions';
import ChatHistory from '../../../../components/chat/chatHistory';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../../../utils/responsive';
// Import chat actions instead of clinicalCase actions
import {
  getCategories,
  getMessagesHistory,
  sendMessage,
} from '../../chat/actions';
// Add ChatFooter and voice recognition hook similar to chat/index.tsx
import ChatFooter from '../../../../components/chat/chatFooter';
import useChatVoiceRecognition from '../../../../utils/hooks/useChatVoiceRecognition';
import {
  selectChat,
  setLoadingMessages,
  setLoadingMessagesEnd,
  setMessages,
} from '../../chat/reducer';
import {ChatItemType} from 'src/interfaces/reducers/chat';
import {CategoryItem} from '../../../../interfaces/components/chatList';
import {ChatCategory} from '../../../../interfaces/api/auth/login';
import {updateConfig} from '../../../auth/login/actions';
import {showGlobalToast} from '../../../../connected-components/toast/actions';
import ClinicalCaseCategories from '../../../../components/clinicalCaseCategories';
import { ChatType } from 'src/utils/enums';
import '../../../../utils/webAnimations';

const AnimatedTouchableOpacity =
  Animated.createAnimatedComponent(TouchableOpacity);

const Container = styled(Animated.View)<{cutHeight?: number}>`
  align-self: center;
  height: 100%;
  z-index: 99;
  border-radius: 16px;
  overflow: hidden;
`;
const WebSideMenuContainer = styled.View<{
  isVisible: boolean;
  width: number;
  height: number;
}>`
  display: ${({isVisible}) => (isVisible ? 'flex' : 'none')};
  width: ${({width}) => width}px;
  height: ${({height}) => height}px;
  background-color: ${({theme}) => theme.screenBackground};
  border-right: 1px solid ${({theme}) => theme.text.grey + '44'};
  overflow-y: auto;
  transition: width 0.3s ease;
`;

const ClinicalCaseContainer = styled.View<{
  headerHeight: number;
  isWeb: boolean;
  paddingHorizontal: number;
  paddingTop: number;
}>`
  flex: 1;
  padding-top: ${({paddingTop}) => paddingTop}px;
  padding-horizontal: ${({isWeb, paddingHorizontal}) =>
    isWeb ? 0 : paddingHorizontal}px;
  ${Platform.OS === 'web' && css`
    height: 100%;
    overflow: hidden;
  `}
`;

const ClinicalCaseScrollView = styled.ScrollView`
  flex: 1;
  ${Platform.OS === 'web' && css`
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  `}
`;

/**
 * EnterClinicalCase component.
 *
 * @returns {React.ReactElement} The EnterClinicalCase component.
 */
const EnterClinicalCase: React.FC<EnterClinicalCaseProps> = ({
  navigation,
  chat,
  route,
  auth,
}): React.ReactElement => {
  const {hdp, wdp, getByScreenSize, isLargeScreen, isMobile} =
    useResponsiveDimensions();
  const {selectedCase, currentChatType} = chat || {cases: []};
  const {config} = useSelector((state: RootState) => state.auth);
  const {tab, tab_id} = route.params || {};

  // Get categories from tab filters or fallback to global chat_categories
  const currentTabId = tab_id ? Number(tab_id) : tab?.id;
  const categoriesToUse = config?.tabs_with_categories?.find(t => t.id === currentTabId)?.categories;

  const theme = useTheme() as Theme;
  const [selected, setSelected] = useState<any>();
  const [isSideMenuVisible, setIsSideMenuVisible] = useState(
    Platform.OS === 'web' && isLargeScreen,
  );
  const [question, setQuestion] = useState<string>(selectedCase || '');
  const {isRecording, startListening, stopListening} =
    useChatVoiceRecognition(setQuestion);
  const sideMenuWidth =
    Platform.OS === 'web' && isLargeScreen ? Math.min(wdp(30), 350) : 0;
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  // Custom header height.
  const headerHeight = useCustomHeaderHeight();
  const tabNavigation = navigation?.getParent(); // Access the parent Tab Navigator

  useEffect(() => {
    if (!tabNavigation) {
      return;
    }

    // Attach the 'blur' listener to the Tab Navigator
    const unsubscribe = tabNavigation.addListener('blur', () => {
      setSelected(undefined);
    });

    // Cleanup the listener on unmount
    return unsubscribe;
  }, [tabNavigation]);

  // // Fetch categories via chat actions using tab?.id
  // const loadCategories = useCallback(async () => {
  //   try {
  //     setIsCategoriesLoading(true);
  //     const res = await getCategories(String(tab?.id || '2'));
  //     setCategories(res || []);
  //   } catch (e) {
  //     setCategories([]);
  //   } finally {
  //     setIsCategoriesLoading(false);
  //   }
  // }, [tab?.id]);
  //
  // useEffect(() => {
  //   loadCategories();
  // }, [loadCategories]);

  const onHistoryItemPress = useCallback(
    async (item: ThreadItemType | ChatItemType) => {
      if (!item?.id) return;
      try {
        navigation?.navigate(screens.content.genioChat, {
          isQNA: false,
          tab_id: String(currentTabId || '2'),
          hideNewChat: true,
          categoryId: -1,
        });
        store.dispatch(
          setMessages({
            messages: [],
            metadata: undefined,
          }),
        );
        const chatId = item.model_id || item.id;
        store.dispatch(setLoadingMessages());
        const messagesResult = await getMessagesHistory(
          Number(chatId),
          0,
          50,
          '',
        );
        store.dispatch(
          setMessages({
            messages: messagesResult?.messages || [],
            metadata: messagesResult?.metadata,
          }),
        );
        store.dispatch(selectChat(chatId.toString()));
      } catch (error) {
        // Clear messages to avoid showing stale data
        store.dispatch(
          setMessages({
            messages: [],
            metadata: undefined,
          }),
        );
        store.dispatch(selectChat((item.model_id || item.id).toString()));
      } finally {
        store.dispatch(setLoadingMessagesEnd());
      }
    },
    [navigation, tab?.id],
  );

  const onQuestionPress = (item: ChatCategory) => {
    setSelected(item);
    const {id: userId, specialty_id} = auth?.currentUser || {};
    const body = {
      question: question || '',
      specialty_id: specialty_id || -1,
      question_id: item?.id,
      user_id: userId || -1,
      thread_id: undefined,
      tab_id: String(currentTabId || '2'),
    };

    sendMessage(body);

    navigation?.navigate(screens.content.genioChat, {
      isQNA: false,
      tab_id: String(currentTabId || '2'),
      drugQuestion: question,
      categoryId: item?.id,
    });
  };

  const onAttachPress = () => {
    if (isRecording) {
      stopListening();
    } else {
      startListening();
    }
  };

  const onSubmit = () => {
    navigation?.navigate(screens.content.genioChat, {
      isQNA: false,
      tab_id: String(currentTabId || '1'),
      drugQuestion: question,
      layout: selected?.layout,
    });
  };

  return (
    <MainLayout
      showOverlay={false}
      isCustomHeader
      enableScroll={false}
      noPadding
      noPaddingHeader>
      <ChatHeader
        onGoBackPress={() => navigation?.navigate(screens.content.homeTab)}
        onHistoryPress={() =>
          toggleSettingsModal({
            title: tr(`chat.chatHistory`),
            component: () => (
              <ChatHistory
                isQNA={false}
                onChatItemPressed={onHistoryItemPress}
                tab_id={String(currentTabId || '2')}
              />
            ),
          })
        }
      />
      <View
        style={{
          flexDirection: isLargeScreen ? 'row' : 'column',
          alignItems: 'center',
          zIndex: 1,
          height: '100%',
        }}>
        {Platform.OS === 'web' && isLargeScreen && (
          <div style={{display: 'flex', height: '100%'}}>
            <WebSideMenuContainer
              isVisible={isSideMenuVisible}
              width={sideMenuWidth}
              height={hdp(100)}>
              <ChatHistory
                isQNA={false}
                onChatItemPressed={onHistoryItemPress}
                tab_id={String(currentTabId || '2')}
              />
            </WebSideMenuContainer>
          </div>
        )}
        <Container
          style={{
            display: 'flex',
            flex: 1,
          }}
          cutHeight={
            headerHeight +
            topInset +
            bottomInset +
            (StatusBar.currentHeight || 0)
          }
          {...(Platform.OS === 'web'
            ? {className: 'screen-fade-in-down'}
            : {entering: FadeInDown, exiting: FadeOutUp})}>
          {(!tab || (tab && (categoriesToUse || []).length > 1)) && (
            <ClinicalCaseContainer
              headerHeight={headerHeight}
              isWeb={Platform.OS === 'web'}
              paddingHorizontal={wdp(3)}
              paddingTop={headerHeight}>
              <ClinicalCaseScrollView
                contentContainerStyle={{flexGrow: 1}}
                showsVerticalScrollIndicator={false}
                scrollEnabled={true}
                bounces={Platform.OS !== 'web'}
                alwaysBounceVertical={false}>
                <ClinicalCaseCategories
                  chat_categories={categoriesToUse}
                  onQuestionPress={onQuestionPress}
                  question={question}
                  showTitle={true}
                  isLoading={!categoriesToUse?.length}
                  isNewChat={true}
                />
              </ClinicalCaseScrollView>
            </ClinicalCaseContainer>
          )}
        </Container>
      </View>
    </MainLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  chat: state.chat,
});
export default connect(mapStateToProps, null)(EnterClinicalCase);
